import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Calendar, Shield, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoachDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/coach`, {
        withCredentials: true
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="coach-dashboard">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-red to-red/90 rounded-xl p-6 mb-8 text-white">
          <div className="flex items-center space-x-3 mb-3">
            <Shield className="w-10 h-10 text-gold" />
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Coach Dashboard
              </h1>
              <p className="text-white/90 text-lg">Welcome, Coach {user?.name?.split(' ')[1]}</p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card data-testid="stat-teams">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">My Teams</p>
                  <p className="text-3xl font-bold text-navy">{dashboard?.teams?.length || 0}</p>
                </div>
                <div className="w-12 h-12 bg-navy/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-navy" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-events">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Upcoming Events</p>
                  <p className="text-3xl font-bold text-navy">{dashboard?.events?.length || 0}</p>
                </div>
                <div className="w-12 h-12 bg-light-blue/10 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-light-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-roster">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Players</p>
                  <p className="text-3xl font-bold text-navy">
                    {dashboard?.teams?.reduce((sum, team) => sum + (team.players?.length || 0), 0) || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-gold" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* My Teams */}
          <Card data-testid="teams-card">
            <CardHeader>
              <CardTitle className="text-navy">My Teams</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.teams && dashboard.teams.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.teams.map((team) => (
                    <div
                      key={team.team_id}
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group"
                      data-testid={`team-card-${team.team_id}`}
                      onClick={() => navigate('/coach/team')}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            {team.logo_url ? (
                              <img 
                                src={team.logo_url} 
                                alt={`${team.name} logo`}
                                className="max-w-full max-h-full object-contain"
                              />
                            ) : (
                              <Shield className="w-6 h-6 text-navy" />
                            )}
                          </div>
                          <p className="font-semibold text-navy text-lg">{team.name}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-navy text-white">
                            {team.players?.length || 0}/{team.roster_limit || team.roster_capacity}
                          </Badge>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-navy transition-colors" />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">{team.division_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No teams assigned</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card data-testid="events-card">
            <CardHeader>
              <CardTitle className="text-navy">Upcoming Events</CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.events && dashboard.events.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.events.map((event) => (
                    <div
                      key={event.event_id}
                      className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      data-testid={`event-${event.event_id}`}
                      onClick={() => navigate(`/events/${event.event_id}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <p className="font-semibold text-navy">{event.title}</p>
                        <Badge className={`${
                          event.event_type === 'game' ? 'bg-red text-white' :
                          event.event_type === 'practice' ? 'bg-light-blue text-white' :
                          'bg-navy text-white'
                        }`}>
                          {event.event_type}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>{format(new Date(event.start_time), 'MMM d, yyyy - h:mm a')}</span>
                        </div>
                        {event.location && <p className="text-xs">{event.location}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No upcoming events</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </ParentLayout>
  );
};

export default CoachDashboard;